import { useEffect, useState } from "react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";
import { RevenueChart } from "./RevenueChart";
import { selectCls } from "./shared";
import { PctSettings } from "./PctSettings";
import { CostInput } from "./CostInput";

export const chf = (v) => (v == null ? "—" : `CHF ${Number(v).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

export const COST_COLS = [
  ["google_ads", "Google ADV", "#4285F4"], ["report_su", "Costo report SU", "#E0262C"], ["meta_ai", "Meta ai", "#0866FF"],
  ["tiktok", "TikTok", "#111111"], ["scraping_web", "Scaping Web", "#f59e0b"], ["mail_massive", "Mail massive", "#8b5cf6"],
];

const exportCsv = (rows, month) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = ["Pagata", "Cliente", "Settore", "Prezzo", "Valuta", "Prezzo CHF", ...COST_COLS.map((c) => `${c[1]} CHF`), "Margine CHF", "Margine %"];
  const body = rows.map((r) => [r.paid_at, r.client, r.skill, r.price, r.currency, r.price_chf, ...COST_COLS.map(([k]) => r.costs[k].chf), r.margin_chf, r.margin_pct].map(esc).join(";"));
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob(["\ufeff" + [head.join(";"), ...body].join("\n")], { type: "text/csv;charset=utf-8" }));
  a.download = `margini-${month || "tutti"}.csv`;
  a.click();
};

const MarginChart = ({ monthly }) => (
  <div data-testid="margins-chart" className="border border-[#d5dbde] p-5">
    <div className="text-[10px] uppercase tracking-widest font-mono text-[#6b7280] mb-4">Ricavi · costi · margine netto (CHF, ultimi 12 mesi)</div>
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 600, height: 256 }}>
        <ComposedChart data={monthly}>
          <CartesianGrid stroke="#d5dbde" vertical={false} />
          <XAxis dataKey="month" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip formatter={(v) => chf(v)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="revenue_chf" name="Ricavi" fill="#2B3036" maxBarSize={28} />
          {COST_COLS.map(([k, label, color]) => <Bar key={k} dataKey={`${k}_chf`} name={label} stackId="costs" fill={color} maxBarSize={28} />)}
          <Line dataKey="margin_chf" name="Margine netto" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const Row = ({ r, onSaved }) => (
  <tr data-testid={`margin-row-${r.id}`} className="border-t border-[#d5dbde] align-top">
    <td className="px-3 py-2 font-mono text-xs">{r.paid_at}</td>
    <td className="px-3 py-2 text-xs">{r.client}</td>
    <td className="px-3 py-2 text-xs max-w-[200px] truncate" title={r.skill}>{r.skill} · <span className="text-[#6b7280]">{r.area}</span></td>
    <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{r.currency} {r.price}</td>
    {COST_COLS.map(([k]) => <td key={k} className="px-2 py-2"><CostInput row={r} k={k} onSaved={onSaved} /></td>)}
    <td data-testid={`margin-value-${r.id}`} className={`px-3 py-2 font-mono text-xs font-semibold whitespace-nowrap ${r.margin_chf < 0 ? "text-[#E0262C]" : "text-[#10b981]"}`}>{chf(r.margin_chf)}</td>
    <td className="px-3 py-2 font-mono text-xs">{r.margin_pct == null ? "—" : `${r.margin_pct}%`}</td>
  </tr>
);

export const MarginsPanel = () => {
  const [month, setMonth] = useState("");
  const [d, setD] = useState(null);
  const load = () => adminApi.margins(month || undefined).then(setD).catch(() => toast.error("Errore caricamento margini"));
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = d?.campaigns || [];
  const tot = (f) => rows.reduce((a, r) => a + (f(r) || 0), 0);
  return (
    <div data-testid="admin-margins" className="space-y-6">
      <div className="grid lg:grid-cols-2 gap-6">
        <RevenueChart />
        <MarginChart monthly={d?.monthly || []} />
      </div>
      {d && <PctSettings settings={d.settings} onSaved={load} />}
      <div className="flex flex-wrap items-center gap-3">
        <select data-testid="margins-month-filter" value={month} onChange={(e) => setMonth(e.target.value)} className={selectCls}>
          <option value="">Tutti i mesi</option>
          {(d?.months || []).map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <span className="text-xs font-mono text-[#5b6168]" data-testid="margins-totals">
          Ricavi {chf(tot((r) => r.price_chf))} · Costi {chf(tot((r) => COST_COLS.reduce((a, [k]) => a + r.costs[k].chf, 0)))} · Margine <b className="text-[#10b981]">{chf(tot((r) => r.margin_chf))}</b>
        </span>
        <button data-testid="margins-export-csv" onClick={() => exportCsv(rows, month)}
          className="ml-auto inline-flex items-center gap-1.5 text-xs border border-[#2B3036] px-3 py-1.5 hover:bg-[#2B3036] hover:text-white transition-colors">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      <p className="text-[11px] text-[#6b7280]">Clicca su un importo per modificarlo (CHF). <span className="text-[#2B3036]">auto</span> = calcolato con le percentuali di "Calcolo margine". ↺ riporta al valore automatico.</p>
      <div className="border border-[#d5dbde] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-[#6b7280] font-mono bg-[#f4f6f7]">
              {["Pagata", "Cliente", "Campagna", "Prezzo", ...COST_COLS.map((c) => c[1]), "Margine", "%"].map((h) => <th key={h} className="px-3 py-3 whitespace-nowrap">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => <Row key={r.id} r={r} onSaved={load} />)}
            {d && !rows.length && <tr><td colSpan={12} className="px-4 py-10 text-center text-[#6b7280] font-mono text-xs">Nessuna campagna pagata nel periodo</td></tr>}
          </tbody>
        </table>
      </div>
      {d && !d.fx_ok && <p className="text-xs text-[#f59e0b]">Cambio valute non disponibile: importi CHF non calcolabili al momento.</p>}
    </div>
  );
};
